import { styled } from '@mui/system';

import { useMemo, useRef, useState } from 'react';

import { TAiPageChatMessageDto } from '@app-velocity/av-api-v2';

import { parseAiResponseFromMessage } from '@app-velocity/common';

import { Discussion } from '../Discussion/Discussion';
import { FigmaDiscussionProvider } from '../Discussion/providers/FigmaDiscussionProvider';

import { useAiChatContext } from '../../hooks/useAiChatContext';
import { SingleAiResponseContext } from './context/SingleAiResponseContext';
import { AiReviewHeader } from '../AiReviewHeader';
import { Explanation } from './components/Explanation';
import { Action } from './components/Action';
import { SystemResponse } from './components/SystemResponse';
import { ConfidenceScore } from './components/ConfidenceScore';
import { useLoopContext } from '../../hooks/useLoopContext';
import { useGetSharedScenarioPage } from '../../../../queries/useGetSharedScenarioPage';

export type SingleAiResponseProps = {
  message: TAiPageChatMessageDto;
  stepNumber: number;
};

const StyledAiReviewContainer = styled('li')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  position: 'relative',
}));

const StyledAiReview = styled('div')({
  border: '1px solid #e8e8e8',
  borderRadius: 8,
  backgroundColor: '#fff',
  overflow: 'hidden',
  position: 'relative',
});

const StyledAiReviewContent = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(4)}`,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const SingleAiResponse = ({
  message,
  stepNumber,
}: SingleAiResponseProps) => {
  const hoveredForTheFirstTime = useRef<boolean>(false);
  // @TODO - appraisals are tightly coupled here only temporarily!
  const { scenario, review } = useAiChatContext();
  const loop = useLoopContext();

  const [collapsed, setCollapsed] = useState(true);
  const [hovered, setHovered] = useState(false);

  // @TODO it is tricky here because this EP is password protected, but it works if owner is opening it
  const { data: fullPage } = useGetSharedScenarioPage(
    scenario.shareToken || '',
    message.pageId || '',
    'report'
  );

  const toggleCollapse = () => {
    setCollapsed((prev) => !prev);
  };

  const data = useMemo(
    () => parseAiResponseFromMessage(message?.content),
    [message]
  );

  const handleHover = () => {
    if (hoveredForTheFirstTime.current) return;
    hoveredForTheFirstTime.current = true;
    setHovered(true);
  };

  const handleBlur = () => {
    setHovered(false);
  };

  if (!fullPage) return null;

  return (
    <SingleAiResponseContext.Provider
      value={{
        collapsed,
        message,
        stepNumber: loop
          ? loop.unaffectedMessagesCount + stepNumber
          : stepNumber,
        response: data,
        page: fullPage,
        partials: scenario.prototypePartials,
        toggleCollapse,
      }}
    >
      <StyledAiReviewContainer>
        <StyledAiReview onMouseEnter={handleHover} onMouseLeave={handleBlur}>
          {data && !data.finished ? <ConfidenceScore opened={hovered} /> : null}
          <StyledAiReviewContent>
            <AiReviewHeader />
            <Action />
            <Explanation />
            <FigmaDiscussionProvider
              shareToken={scenario.shareToken}
              reviewId={review?.reviewId}
              pageId={message.pageId}
              messageId={message.id}
              enabled={!collapsed}
            >
              <Discussion
                challenge={scenario.introMessage}
                takenAction={data?.nextAction}
                element={data?.nextActionElementLabel}
                explanation={data?.explanation}
                hidden={collapsed}
              />
            </FigmaDiscussionProvider>
            <SystemResponse />
          </StyledAiReviewContent>
        </StyledAiReview>
      </StyledAiReviewContainer>
    </SingleAiResponseContext.Provider>
  );
};
